import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate, useLocation, useParams, useSearchParams } from "react-router-dom";
import { getAuthInstance } from "./firebase";
import { useAuthState } from "react-firebase-hooks/auth";
import { signOut } from "firebase/auth";
import { Edit2, ChevronRight, Zap, Check, X, Copy, HelpCircle, House, User, LogOut, CreditCard, Eye, Monitor, Smartphone, PaintbrushVertical, Users, Key, Undo2, Redo2 } from "lucide-react";
import { VscSend } from "react-icons/vsc";
import { RiLink } from "react-icons/ri";
import { PiArrowBendUpRight } from "react-icons/pi";
import { IoCloudDoneOutline } from "react-icons/io5";
import { HiOutlineSlash } from "react-icons/hi2";
import { getCurrentSubscription, saveSurvey, getSurvey, renameBrandkit } from "./serverComm";
import { createPortal } from "react-dom";
import { ModalContainer } from "./components/ModalContainer";
import BillingTab from "./components/settings/BillingTab";
import ResponseCounter from "./components/shared/ResponseCounter";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import {
  setActiveSurvey,
  setActiveWorkspace,
  setActiveBrandkit,
  setHasChanges,
  updateAiUses,
  updateCurrentSubscription,
  updateTotalResponses,
  updateSurveyCount,
  updateSeats,
  updateUsageLimits,
  setUser,
} from "../../store/appSlice";
import Notification from "./Notification";
import { Tooltip } from "./components/ui/Tooltip";
import SurveyRenameModal from "./components/SurveyRenameModal";
import WhatsNewPopup from "./components/WhatsNewPopup";
import { WHATS_NEW_VERSION } from "./assets/whatsNewData";
import ShareSurveyModal from "./components/SurveyDesignerComponents/ShareSurveyModal";
import toast from "react-hot-toast";
import OrgSwitcher from "./components/OrgSwitcher";

// Add CSS for fade-in animation
const fadeInStyle = `
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(-2px); }
    to { opacity: 1; transform: translateY(0); }
  }
  
  @keyframes dropdownOpen {
    from { 
      opacity: 0; 
      transform: translateY(-8px); 
    }
    to { 
      opacity: 1; 
      transform: translateY(0); 
    }
  }
  
  .animate-dropdown-open {
    animation: dropdownOpen 0.2s ease-out forwards;
  }
  
  .animate-fade-in {
    animation: fadeIn 0.2s ease-out forwards;
  }
`;

// Inject the CSS if it doesn't exist
if (!document.querySelector('#fade-in-styles')) {
  const style = document.createElement('style');
  style.id = 'fade-in-styles';
  style.textContent = fadeInStyle;
  document.head.appendChild(style);
}

// Logo URL constant to ensure consistent caching
const LOGO_URL = "https://firebasestorage.googleapis.com/v0/b/surveyappreact.firebasestorage.app/o/default%2Flogo_pb.svg?alt=media&token=e0035fbc-fa3e-4b6c-8ae5-81eac333c8e4";

// Preload logo image to prevent flickering during navigation
if (typeof window !== 'undefined') {
  const link = document.querySelector('link[rel="preload"][href*="logo_pb.svg"]');
  if (!link) {
    const preloadLink = document.createElement('link');
    preloadLink.rel = 'preload';
    preloadLink.as = 'image';
    preloadLink.href = LOGO_URL;
    document.head.appendChild(preloadLink);
  }
  // Also preload via Image object for immediate caching
  const img = new Image();
  img.src = LOGO_URL;
}

const LogoBlack = () => (
  <img
    src={LOGO_URL}
    alt="PollBolt Logo"
    className="h-full w-full"
  />
);

const ProfileDropdownItem = ({
  icon: Icon,
  label,
  onClick,
  variant = "default",
}) => (
  <button
    onClick={onClick}
    className={`flex items-center px-3 py-2 text-sm transition-all duration-200 rounded-lg mx-3 my-0.5
      ${
        variant === "danger"
          ? "text-red-600 hover:bg-red-50"
          : "text-gray-700 hover:bg-[#f2f2fb]"
      }`}
    style={{ width: 'calc(100% - 24px)' }}
  >
    <Icon className="w-4 h-4 mr-3 text-ink-disabled" />
    {label}
  </button>
);

const ThemePublishButton = () => {
  const [buttonState, setButtonState] = useState('idle'); // 'idle', 'loading', 'success'
  const loadingStartTime = useRef(0);

  useEffect(() => {
    const handleThemePublished = () => {
      // Ensure minimum loading time of 600ms for visual feedback
      const elapsed = Date.now() - loadingStartTime.current;
      const remaining = Math.max(0, 600 - elapsed);
      
      setTimeout(() => {
        setButtonState('success');
        toast.success('Theme saved! Ready to use ✨', {
          duration: 2500,
        });
        setTimeout(() => setButtonState('idle'), 1500);
      }, remaining);
    };

    const handleThemeError = () => {
      const elapsed = Date.now() - loadingStartTime.current;
      const remaining = Math.max(0, 600 - elapsed);
      
      setTimeout(() => {
        setButtonState('idle');
        toast.error('Failed to save theme');
      }, remaining);
    };

    window.addEventListener('themePublished', handleThemePublished);
    window.addEventListener('themePublishError', handleThemeError);
    return () => {
      window.removeEventListener('themePublished', handleThemePublished);
      window.removeEventListener('themePublishError', handleThemeError);
    };
  }, []);

  const handleClick = () => {
    if (buttonState !== 'idle') return;
    loadingStartTime.current = Date.now();
    setButtonState('loading');
    const publishEvent = new CustomEvent('publishTheme');
    window.dispatchEvent(publishEvent);
  };

  return (
    <button
      onClick={handleClick}
      disabled={buttonState === 'loading'}
      className={`flex items-center justify-center px-4 h-10 min-w-[90px] rounded-lg transition-all duration-300 ${
        buttonState === 'success' 
          ? 'bg-green-500 text-white' 
          : 'bg-black text-white hover:bg-gray-800'
      }`}
    >
      {buttonState === 'loading' && (
        <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      )}
      {buttonState === 'success' && (
        <Check className="h-5 w-5 text-white" />
      )}
      {buttonState === 'idle' && (
        <span className="text-sm font-normal" style={{ fontSize: '14px' }}>Publish</span>
      )}
    </button>
  );
};

const NavBar = React.memo(() => {
  const navigate = useNavigate();
  const { activeSurvey, activeWorkspace, activeBrandkit, hasChanges, currentSubscription, totalResponses } =
    useSelector((state) => state.app);
  const isReachedLimit = currentSubscription?.responseLimit != null && totalResponses >= currentSubscription.responseLimit;

  const dispatch = useDispatch();
  const auth = getAuthInstance();
  const [user] = useAuthState(auth);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const profileDropdownRef = useRef(null);
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { surveyId } = useParams();
  const [isEditingName, setIsEditingName] = useState(false);
  const [saveStatus, setSaveStatus] = useState("idle"); // 'idle', 'saving', 'saved'
  const [isPricingModalOpen, setIsPricingModalOpen] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewTimestamp, setPreviewTimestamp] = useState(Date.now());
  const [previewDevice, setPreviewDevice] = useState('desktop'); // 'desktop' or 'mobile'
  const [designerPreviewMode, setDesignerPreviewMode] = useState('desktop');
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [copyStatus, setCopyStatus] = useState("idle"); // 'idle', 'copying', 'copied'
  const [surveyTitle, setSurveyTitle] = useState(activeSurvey?.title || "");
  const [linkButtonState, setLinkButtonState] = useState("idle"); // 'idle', 'clicked'
  const [isLinkPressed, setIsLinkPressed] = useState(false);
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const [tooltipContent, setTooltipContent] = useState("Click to copy form link");
  const [isSurveyRenameModalOpen, setIsSurveyRenameModalOpen] = useState(false);
  const [isBrandkitRenameModalOpen, setIsBrandkitRenameModalOpen] = useState(false);
  const [isWhatsNewOpen, setIsWhatsNewOpen] = useState(false);
  const [hasWhatsNewBadge, setHasWhatsNewBadge] = useState(() => {
    try {
      return localStorage.getItem('pollbolt_whats_new_seen') !== WHATS_NEW_VERSION;
    } catch { return true; }
  });

  const handleOpenWhatsNew = () => {
    setIsWhatsNewOpen(true);
    setHasWhatsNewBadge(false);
    try { localStorage.setItem('pollbolt_whats_new_seen', WHATS_NEW_VERSION); } catch {}
  };

  // Banner visibility is session-based, not persistent
  // Load banner visibility from sessionStorage, default to true if not set
  const [isFreePlanBannerVisible, setIsFreePlanBannerVisible] = useState(() => {
    const saved = sessionStorage.getItem('freePlanBannerVisible');
    return saved !== null ? saved === 'true' : true;
  });
  const [shouldShowPublishButton, setShouldShowPublishButton] = useState(false);
  const [shouldWrapTabs, setShouldWrapTabs] = useState(false);
  const copyTimeoutRef = useRef(null);
  const navRef = useRef(null);
  const isSurveysPage = location.pathname === "/workspaces";
  const isProfilePage = location.pathname === "/profile";
  const isBrandkitPage = location.pathname.startsWith("/brandkit");
  const isThemeCreatePage = location.pathname.includes("/themes/create");
  const themeName = searchParams.get("name") || "Untitled Theme";

  // Sync banner visibility state with sessionStorage when location changes
  useEffect(() => {
    const saved = sessionStorage.getItem('freePlanBannerVisible');
    if (saved !== null) {
      setIsFreePlanBannerVisible(saved === 'true');
    }
  }, [location.pathname]);

  useEffect(() => {
    if (activeSurvey?.title) {
      setSurveyTitle(activeSurvey.title);
    }
  }, [activeSurvey]);

  // Update CSS variable for navbar height
  useEffect(() => {
    const updateNavbarHeight = () => {
      const isSurveyDesignerRoute = location.pathname.includes("/survey/");
      const isMobile = window.innerWidth < 768;
      let navbarHeight = '64px';
      
      if (isSurveyDesignerRoute) {
        if (shouldWrapTabs) {
          // When tabs wrap, navbar is 64px (h-16) + 48px (h-12 for tabs) = 112px
          navbarHeight = '112px';
        } else if (isMobile) {
          // On mobile when tabs don't wrap, navbar is 64px + 48px (mobile tabs below) = 112px
          navbarHeight = '112px';
        } else {
          // Desktop when tabs don't wrap, navbar is just 64px
          navbarHeight = '64px';
        }
      }
      
      document.documentElement.style.setProperty('--navbar-height', navbarHeight);
    };
    
    updateNavbarHeight();
    
    window.addEventListener('resize', updateNavbarHeight);
    
    return () => {
      window.removeEventListener('resize', updateNavbarHeight);
      document.documentElement.style.setProperty('--navbar-height', '64px');
    };
  }, [shouldWrapTabs, location.pathname]);

  // Check if tabs should wrap to new line when survey name is long
  useEffect(() => {
    const isSurveyDesignerRoute = location.pathname.includes("/survey/");
    const currentSurveyId = surveyId || location.pathname.split("/survey/")[1]?.split("/")[0];
    
    if (!isSurveyDesignerRoute || !currentSurveyId) {
      setShouldWrapTabs(false);
      return;
    }

    const checkWrap = () => {
      if (!navRef.current) return;
      
      const nav = navRef.current;
      const navWidth = nav.offsetWidth;
      const isMobile = window.innerWidth < 768; // md breakpoint
      
      // Get the left section (survey name area) and right section
      const leftSection = nav.querySelector('.flex.items-center.h-16.flex-1');
      const rightSection = nav.querySelector('.flex.items-center.h-full.flex-1.justify-end');
      
      if (!leftSection || !rightSection) return;
      
      // Get the survey name element to check its actual width
      const surveyNameElement = leftSection.querySelector('span[title]');
      if (!surveyNameElement) return;
      
      // Get the actual rendered width of the survey name
      const surveyNameWidth = surveyNameElement.offsetWidth;
      
      // Measure actual widths
      const logoWidth = 60;
      const workspaceElement = leftSection.querySelector('span[title]')?.previousElementSibling?.previousElementSibling?.querySelector('span');
      const workspaceWidth = workspaceElement ? Math.min(workspaceElement.offsetWidth, isMobile ? 80 : 220) : (isMobile ? 80 : 150);
      
      // Separators and spacing
      const separatorsWidth = 40; // ChevronRight and HiOutlineSlash
      const leftPadding = 20;
      
      // Calculate actual left section width
      const leftSectionWidth = logoWidth + workspaceWidth + separatorsWidth + surveyNameWidth + leftPadding;
      
      // Right section: measure actual width
      const rightContentWidth = rightSection.scrollWidth || (isMobile ? 150 : 250);
      
      // Tabs need approximately 320px width (4 tabs * ~80px each)
      const tabsWidth = 320;
      
      // Minimum spacing between elements - reduced to wrap earlier
      const minSpacing = isMobile ? 20 : 50;
      
      // Calculate if we need to wrap
      // Wrap if: left section + tabs + right section + spacing exceeds nav width
      // OR if survey name would overlap with tabs (more aggressive check)
      const totalNeeded = leftSectionWidth + tabsWidth + rightContentWidth + minSpacing;
      
      // More aggressive wrapping: wrap if survey name + tabs would overlap
      // Check if survey name would overlap with tabs area (center of navbar)
      const tabsStartPosition = navWidth / 2 - tabsWidth / 2; // Tabs are centered
      const surveyNameEndPosition = leftSectionWidth;
      
      // Wrap if survey name would extend into tabs area OR if total exceeds width
      const wouldOverlap = surveyNameEndPosition > (tabsStartPosition - minSpacing);
      const exceedsWidth = navWidth < totalNeeded;
      
      const shouldWrap = wouldOverlap || exceedsWidth;
      
      setShouldWrapTabs(shouldWrap);
    };

    // Use setTimeout to ensure DOM is ready
    const timeoutId = setTimeout(checkWrap, 200);
    
    const resizeObserver = new ResizeObserver(() => {
      setTimeout(checkWrap, 100);
    });
    
    if (navRef.current) {
      resizeObserver.observe(navRef.current);
    }

    window.addEventListener('resize', checkWrap);
    
    return () => {
      clearTimeout(timeoutId);
      resizeObserver.disconnect();
      window.removeEventListener('resize', checkWrap);
    };
  }, [location.pathname, surveyId, surveyTitle]);

  // Cleanup timeout on component unmount or navigation
  useEffect(() => {
    return () => {
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
        copyTimeoutRef.current = null;
      }
      setCopyStatus("idle");
      setLinkButtonState("idle");
      setShouldShowPublishButton(false); // Reset publish button visibility on navigation
      toast.dismiss();
    };
  }, [location.pathname]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        profileDropdownRef.current &&
        !profileDropdownRef.current.contains(event.target)
      ) {
        setIsProfileDropdownOpen(false);
      }
    };
    const fetchCurrentSubscription = async () => {
      const workspaceId = new URLSearchParams(location.search).get('w');
      const subscription = await getCurrentSubscription(workspaceId);
      dispatch(updateCurrentSubscription(subscription?.subscription || null));
      dispatch(updateTotalResponses(subscription?.totalResponses || 0));
      dispatch(updateAiUses(subscription?.aiUses || 0));
      dispatch(updateSurveyCount(subscription?.surveyCount || 0));
      dispatch(updateSeats(subscription?.seats || { used: 1, limit: 1 }));
      dispatch(updateUsageLimits(subscription?.limits || null));
    };

    // Auto-save event listener - show save status
    const handleAutoPublish = (event) => {
      // Only show save status if we're on a survey designer route
      if (location.pathname.includes("/survey/") && activeSurvey?.surveyId) {
        // Show "Saving..." status
        setSaveStatus("saving");
        
        // After a short delay, show "Saved!" status
        setTimeout(() => {
          setSaveStatus("saved");
          setTimeout(() => dispatch(setHasChanges(false)), 1000);
          
          // Hide the status after 2.5 seconds
          setTimeout(() => {
            setSaveStatus("idle");
          }, 2500);
        }, 800); // Show saving for 800ms
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("autoPublish", handleAutoPublish);

    // getting subscription details
    fetchCurrentSubscription();
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("autoPublish", handleAutoPublish);
    };
  }, [location.pathname, location.search]); // Re-fetch when workspace changes (location.search includes ?w=workspaceId)

  useEffect(() => {
    const handleDesignerPreviewModeChanged = (event) => {
      if (event.detail?.previewMode) {
        setDesignerPreviewMode(event.detail.previewMode);
      }
    };

    const handleDesignerUndoStateChanged = (event) => {
      if (event.detail) {
        setCanUndo(!!event.detail.canUndo);
        setCanRedo(!!event.detail.canRedo);
      }
    };

    window.addEventListener("designerPreviewModeChanged", handleDesignerPreviewModeChanged);
    window.addEventListener("designerUndoStateChanged", handleDesignerUndoStateChanged);
    return () => {
      window.removeEventListener("designerPreviewModeChanged", handleDesignerPreviewModeChanged);
      window.removeEventListener("designerUndoStateChanged", handleDesignerUndoStateChanged);
    };
  }, []);

  const isSurveyViewerRoute = location.pathname.startsWith("/s/");

  // Add useEffect to handle cleanup when navigating away
  // Add cleanup to the existing navigate function
  const handleNavigateAway = () => {
    // Only navigate if not already on workspaces page
    if (location.pathname !== "/workspaces") {
      // If we have an active workspace, navigate to it specifically
      if (activeWorkspace?._id) {
        navigate(`/workspaces?w=${activeWorkspace._id}`);
      } else {
        navigate("/workspaces");
      }
    }
  };

  if (isSurveyViewerRoute) {
    return null;
  }

  const handleLogout = async () => {
    try {
      // Clear all localStorage cache data
      const keysToRemove = [];
      
      // Iterate through all localStorage keys to find survey-specific data
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (
          key === 'workspaces_cache' ||
          key.startsWith('workspaces_cache_') ||
          key === 'pollbolt_user_id' ||
          key.startsWith('survey_') ||
          key.includes('_cache') ||
          key.includes('_responded') ||
          key.includes('_viewed') ||
          key.includes('_ending_screen') ||
          key.includes('_score') ||
          key.includes('_background_color')
        )) {
          keysToRemove.push(key);
        }
      }
      
      // Remove all identified keys
      keysToRemove.forEach(key => localStorage.removeItem(key));
      
      // Reset Redux state to initial values
      dispatch(setActiveSurvey(null));
      dispatch(setActiveWorkspace(null));
      dispatch(updateCurrentSubscription(null));
      dispatch(updateTotalResponses(0));
      dispatch(updateAiUses(0));
      dispatch(setUser(null));
      
      // Sign out from Firebase
      await signOut(auth);
      setIsProfileDropdownOpen(false);
      navigate("/");
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  const handleUpgradeClick = () => {
    navigate('/profile?tab=billing');
  };

  const handleNameEdit = () => {
    setIsSurveyRenameModalOpen(true);
  };

  const handleRenameSubmit = async (surveyId, newTitle) => {
    try {
      setSurveyTitle(newTitle);
      
      const surveyData = {
        surveyId: activeSurvey?.surveyId || surveyId,
        title: newTitle,
        questions: activeSurvey?.questions || [],
        styles: activeSurvey?.styles || {},
        endingScreens: activeSurvey?.endingScreens || [],
        welcomeScreen: activeSurvey?.welcomeScreen || null,
      };

      const response = await saveSurvey(surveyData);
      dispatch(setActiveSurvey(response));
      toast.success("Survey renamed successfully!");

      // Trigger auto-publish after renaming survey
      setTimeout(() => {
        const publishEvent = new CustomEvent('autoPublish', {
          detail: { 
            reason: 'surveyRenamed',
            oldTitle: activeSurvey?.title,
            newTitle: newTitle
          }
        });
        window.dispatchEvent(publishEvent);
      }, 500); // Shorter delay for faster response
    } catch (error) {
      console.error("Error renaming survey:", error);
      toast.error("Failed to rename survey");
    }
  };

  const handleBrandkitRenameSubmit = async (brandkitId, newName) => {
    try {
      await renameBrandkit(brandkitId, newName);
      dispatch(setActiveBrandkit({ ...(activeBrandkit || {}), _id: brandkitId, name: newName }));
      toast.success("Brandkit renamed successfully");
    } catch (error) {
      console.error("Error renaming brandkit:", error);
      toast.error("Failed to rename brandkit");
    }
  };

  const handleNameSave = async () => {
    try {
      if (!activeSurvey || !activeSurvey.surveyId) {
        throw new Error("Survey ID not found");
      }
      // debouncedSave(surveyTitle);

      const surveyData = {
        surveyId: activeSurvey?.surveyId || "",
        title: surveyTitle,
        questions: activeSurvey?.questions || [],
        styles: activeSurvey?.styles || {},
        endingScreens: activeSurvey?.endingScreens || [],
        welcomeScreen: activeSurvey?.welcomeScreen || null,
      };

      const response = await saveSurvey(surveyData);
      dispatch(setActiveSurvey(response));
    } catch (error) {
      console.error("Error saving survey title:", error);
    }
  };

  const handleSaveSurvey = async () => {
    try {
      const currentSurveyId = activeSurvey?.surveyId;
      if (!currentSurveyId) {
        throw new Error("Survey ID not found");
      }
      setSaveStatus("saving");
      const currentSurveyData = activeSurvey;

      // Check if we're on survey designer page
      const isSurveyDesignerPage = location.pathname.includes('/survey/') && 
                                   location.pathname.includes('/design') || 
                                   location.pathname.match(/\/survey\/[^/]+$/) && 
                                   !location.pathname.includes('/analytics') && 
                                   !location.pathname.includes('/responses') &&
                                   !location.pathname.includes('/settings');

      // For survey designer page, check if we've already shown the share modal for this survey
      let isFirstTimePublishing = false;
      if (isSurveyDesignerPage) {
        const shareModalShownKey = `shareModalShown_${currentSurveyId}`;
        const hasShownShareModal = localStorage.getItem(shareModalShownKey);
        
        if (!hasShownShareModal) {
          isFirstTimePublishing = true;
          // Mark that we're about to show the share modal for this survey
          localStorage.setItem(shareModalShownKey, 'true');
        }
      }

      // Create the proper survey data object with all style properties
      const surveyData = {
        surveyId: currentSurveyId,
        title: currentSurveyData.title,
        questions: currentSurveyData.questions || [],
        endingScreens: currentSurveyData.endingScreens || [],
        welcomeScreen: currentSurveyData.welcomeScreen || null,
        styles: {
          ...currentSurveyData.styles,
          fontFamily: currentSurveyData.styles?.fontFamily || "Inter",
          submitButtonColor:
            currentSurveyData.styles?.submitButtonColor || "#4F46E5",
          submitButtonTextColor:
            currentSurveyData.styles?.submitButtonTextColor || "#FFFFFF",
          backgroundColor:
            currentSurveyData.styles?.backgroundColor || "#FFFFFF",
          questionTextColor:
            currentSurveyData.styles?.questionTextColor || "#000000",
          optionTextColor:
            currentSurveyData.styles?.optionTextColor || "#4B5563",
          useCustomSubmitText:
            currentSurveyData.styles?.useCustomSubmitText ?? false,
          customSubmitText: currentSurveyData.styles?.customSubmitText || "",
          backgroundImage: currentSurveyData.styles?.backgroundImage || "",
          logo: currentSurveyData.styles?.logo || {
            url: "",
            size: "md",
            alignment: "left",
          },
        },
        // Only set isPublished to true if this is the first time publishing
        ...(isFirstTimePublishing && { isPublished: true }),
      };

      // Save survey with the proper data structure
      const savedSurvey = await saveSurvey(surveyData);
      dispatch(setActiveSurvey(savedSurvey));

      // Only dispatch publish event AFTER successful save and only if it's first time
      if (isFirstTimePublishing) {
        const publishEvent = new CustomEvent('surveyPublish', {
          detail: { isFirstPublish: true }
        });
        window.dispatchEvent(publishEvent);
      }

      setSaveStatus("saved");
      setTimeout(() => dispatch(setHasChanges(false)), 1000);
      setTimeout(() => {
        setSaveStatus("idle");
        setShouldShowPublishButton(false); // Hide the button after publish is complete
      }, 2500);
    } catch (error) {
      console.error("Error saving survey:", error);
      setSaveStatus("idle");
    }
  };

  const playNotificationSound = () => {
    // Create a short notification sound using Web Audio API
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    
    oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
    oscillator.frequency.setValueAtTime(600, audioContext.currentTime + 0.1);
    
    gainNode.gain.setValueAtTime(0.1, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.2);
    
    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.2);
  };

  const resetCopyState = () => {
    setCopyStatus("idle");
    setLinkButtonState("idle");
    if (copyTimeoutRef.current) {
      clearTimeout(copyTimeoutRef.current);
      copyTimeoutRef.current = null;
    }
  };

  const handleCopyLink = () => {
    // Always reset state first and dismiss all toasts
    resetCopyState();
    toast.dismiss();

    const currentSurveyId = surveyId || location.pathname.split("/survey/")[1]?.split("/")[0];
    const link = `${window.location.origin}/s/${currentSurveyId}`;
    setLinkButtonState("clicked");
    setIsTooltipOpen(false);
    
    navigator.clipboard
      .writeText(link)
      .then(() => {
        setCopyStatus("copied");
        
        // Show toast notification with sound
        const toastId = toast.success("Link copied!", {
          position: 'top-right',
          style: {
            marginTop: '60px',
            zIndex: 40
          },
          duration: 1500
        });
        
        playNotificationSound();
        
        // Auto reset after 1.5 seconds
        copyTimeoutRef.current = setTimeout(() => {
          resetCopyState();
          toast.dismiss(toastId);
        }, 1500);
      })
      .catch((error) => {
        console.error("Error copying survey link:", error);
        resetCopyState();
        setIsTooltipOpen(false);
        toast.error("Failed to copy link", { duration: 2000 });
      });
  };

  const handleLinkMouseDown = () => {
    setIsLinkPressed(true);
  };

  const handleLinkMouseUp = () => {
    setIsLinkPressed(false);
  };

  const handleLinkMouseLeave = () => {
    // Only hide tooltip on mouse leave if we're not in clicked state
    if (linkButtonState !== "clicked") {
      setIsTooltipOpen(false);
    }
  };

  const handleLinkMouseEnterFixed = () => {
    // Only show tooltip if we're not in clicked state (during toast display)
    if (linkButtonState !== "clicked") {
      setIsTooltipOpen(true);
    }
  };

  const currentTab = location.pathname.includes("/analytics")
    ? "Results"
    : location.pathname.includes("/settings")
    ? "Settings"
    : location.pathname.includes("/logic")
    ? "Logic"
    : "Create";

  // Get the current survey ID from the URL path if not available in params
  const currentSurveyId =
    surveyId || location.pathname.split("/survey/")[1]?.split("/")[0];

  const isSurveyDesignerRoute = location.pathname.includes("/survey/");
  const isNewSurveyRoute = location.pathname.includes("/workspaces/new") || location.pathname.includes("/surveys/new");
  const isOnboardingRoute = location.pathname.includes("/onboarding");
  
  // Check for specific routes that should have white background
  const isLogicRoute = location.pathname.includes("/logic");
  const isSettingsRoute = location.pathname.includes("/settings");
  const isResultsRoute = location.pathname.includes("/analytics");
  const isCreateRoute = isSurveyDesignerRoute && !isLogicRoute && !isSettingsRoute && !isResultsRoute;
  const shouldHaveWhiteBackground = isLogicRoute || isSettingsRoute || isResultsRoute || isCreateRoute;

  return (
    <>
      <div
        className={`fixed top-0 left-0 right-0 ${isNewSurveyRoute ? 'bg-transparent backdrop-blur-sm' : 'bg-white'}`}
        style={{ zIndex: 40 }}
      >
        {(location.pathname.includes("/workspaces") || location.pathname.includes("/surveys")) && isReachedLimit && !isNewSurveyRoute && (
          <div className="px-3 pt-2">
            <div className="bg-[#FEF2F2] rounded-lg flex items-center justify-center px-4 py-2.5 mx-auto relative">
              <div className="flex items-center gap-2">
                <span className="text-lg">⚠️</span>
                <span className="text-[#DC2626] font-normal text-[15px]">
                  Response Limit: <span className="font-semibold">Reached</span> → You've collected {totalResponses} responses. Upgrade to continue.{" "}
                  <button
                    onClick={handleUpgradeClick}
                    className="text-[#DC2626] underline hover:text-[#B91C1C] font-medium text-[15px]"
                  >
                    Upgrade now!
                  </button>
                </span>
              </div>
            </div>
          </div>
        )}
        {location.pathname.includes("/workspaces") && 
         currentSubscription?.plan?.type === "free" && 
         !isReachedLimit && 
         isFreePlanBannerVisible &&
         !isNewSurveyRoute && (
          <div className="px-3 pt-2">
            <div className="bg-[#FFF7E6] rounded-lg flex items-center justify-center px-4 py-2.5 mx-auto relative">
              <div className="flex items-center gap-2">
                <span className="text-lg">👉</span>
                <span className="text-[#92400E] font-normal text-[15px]">
                  Your Account: <span className="font-semibold">Free</span> → Get powerful features. Collect more responses.{" "}
                  <button
                    onClick={handleUpgradeClick}
                    className="text-[#92400E] underline hover:text-[#78350F] font-medium text-[15px]"
                  >
                    Upgrade now!
                  </button>
                </span>
              </div>
              <button
                onClick={() => {
                  setIsFreePlanBannerVisible(false);
                  // Persist banner closed state in sessionStorage
                  sessionStorage.setItem('freePlanBannerVisible', 'false');
                  // Dispatch custom event to notify other components
                  window.dispatchEvent(new CustomEvent('freePlanBannerClosed'));
                }}
                className="absolute right-4 text-[#92400E] hover:text-[#78350F] transition-colors"
                aria-label="Close banner"
              >
                <X size={20} />
              </button>
            </div>
          </div>
        )}
        {isOnboardingRoute ? (
          <nav className="flex items-center justify-between h-16 w-full">
            {/* Logo removed from onboarding route as requested */}
          </nav>
        ) : (
          <nav ref={navRef} className={`flex flex-wrap items-center justify-between min-h-16 w-full ${
            (isProfilePage || isBrandkitPage || isSettingsRoute || isResultsRoute || isLogicRoute || isCreateRoute) && !isNewSurveyRoute ? 'border-b border-gray-200' : ''
          }`}>
            {/* Logo - always rendered to prevent flickering */}
            <button
              onClick={handleNavigateAway}
              className="h-16 pl-3 w-[60px] flex items-center justify-center group flex-shrink-0 absolute left-0 z-10"
            >
              <img
                src={LOGO_URL}
                alt="Logo"
                className="h-8 w-auto"
                loading="eager"
              />
            </button>

            {isSurveyDesignerRoute ? (
              <>
                <div className="flex items-center h-16 flex-1 min-w-0 max-w-full">
                  {/* Spacer for logo */}
                  <div className="w-[60px] pl-3 flex-shrink-0" />
                  <div className="relative ml-1 min-w-0 flex-1">
                    <div className="flex items-center space-x-2 text-sm font-normal">
                      <div className="flex-shrink-0">
                        <HiOutlineSlash size={18} className="text-gray-300" />
                      </div>
                      <button
                        onClick={handleNavigateAway}
                        className="cursor-pointer items-center transition-none font-inter flex-shrink-0 hidden md:flex"
                      >
                        <span className="text-sm font-normal text-gray-900 truncate max-w-[80px] md:max-w-[220px]" title={activeWorkspace?.name || "All Forms"}>
                          {activeWorkspace?.name || "All Forms"}
                        </span>
                      </button>
                      {activeSurvey && (
                        <>
                          <ChevronRight
                            size={16}
                            className="text-gray-400 mx-1 flex-shrink-0 hidden md:block"
                          />
                          <div className="group relative inline-flex items-center min-w-0 flex-1">
                            <div className="flex items-center gap-1.5">
                              <span
                                onClick={handleNameEdit}
                                className="text-sm font-normal text-gray-900 cursor-pointer hover:text-gray-700 truncate block"
                                style={{ maxWidth: 'clamp(100px, 12vw, 350px)' }}
                                title={surveyTitle}
                              >
                                {surveyTitle}
                              </span>
                              <Edit2
                                size={14}
                                className="hidden md:block text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer flex-shrink-0"
                                onClick={handleNameEdit}
                              />
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                {/* Tabs - will wrap to new line when needed (desktop and mobile) */}
                {currentSurveyId && (
                  <>
                    {shouldWrapTabs ? (
                      <div className="flex flex-[1_1_100%] justify-center order-3 border-t border-gray-200 md:border-b-0 border-b border-gray-200">
                        <div className="flex items-center h-12 gap-4">
                          {[
                            {
                              name: "Create",
                              to: `/survey/${currentSurveyId}`,
                            },
                            {
                              name: "Logic",
                              to: `/survey/${currentSurveyId}/logic`,
                            },
                            {
                              name: "Settings",
                              to: `/survey/${currentSurveyId}/settings`,
                            },
                            {
                              name: "Results",
                              to: `/survey/${currentSurveyId}/analytics`,
                            },
                          ].map((elem) => {
                            return (
                              <SurveyTabs
                                key={elem.name}
                                elem={elem}
                                currentTab={currentTab}
                                isReachedLimit={isReachedLimit}
                                responseCount={activeSurvey?.responseCount}
                              />
                            );
                          })}
                        </div>
                      </div>
                    ) : (
                      <div className="hidden md:flex absolute left-1/2 transform -translate-x-1/2">
                        <div className="flex items-center h-16 gap-4">
                          {[
                            {
                              name: "Create",
                              to: `/survey/${currentSurveyId}`,
                            },
                            {
                              name: "Logic",
                              to: `/survey/${currentSurveyId}/logic`,
                            },
                            {
                              name: "Settings",
                              to: `/survey/${currentSurveyId}/settings`,
                            },
                            {
                              name: "Results",
                              to: `/survey/${currentSurveyId}/analytics`,
                            },
                          ].map((elem) => {
                            return (
                              <SurveyTabs
                                key={elem.name}
                                elem={elem}
                                currentTab={currentTab}
                                isReachedLimit={isReachedLimit}
                                responseCount={activeSurvey?.responseCount}
                              />
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </>
            ) : isNewSurveyRoute ? (
              <div className="flex items-center h-full flex-1">
                <div className="w-[60px] pl-3 flex-shrink-0" />
              </div>
            ) : (
              <div
                className={`border-r border-transparent flex items-center h-full`}
              >
                {/* Spacer for logo */}
                <div className="w-[60px] pl-3 flex-shrink-0" />
                <OrgSwitcher />
                {isProfilePage && (
                  <div className="relative ml-1 hidden md:block">
                    <div className="flex items-center space-x-2 text-sm font-normal">
                      <div className="flex-shrink-0">
                        <HiOutlineSlash size={18} className="text-gray-300" />
                      </div>
                      <span className="text-sm font-normal text-gray-900 font-inter">
                        {(() => {
                          const searchParams = new URLSearchParams(location.search);
                          const tab = searchParams.get('tab') || 'profile';
                          const tabNames = {
                            'profile': 'My profile',
                            'billing': 'Subscription & Billing'
                          };
                          return tabNames[tab] || 'My profile';
                        })()}
                      </span>
                    </div>
                  </div>
                )}
                {isBrandkitPage && (
                  <div className="relative ml-1 hidden md:block">
                    <div className="flex items-center space-x-2 text-sm font-normal whitespace-nowrap">
                      <div className="flex-shrink-0">
                        <HiOutlineSlash size={18} className="text-gray-300" />
                      </div>
                      {location.pathname === "/brandkit" ? (
                        <span className="text-sm font-normal text-gray-900 font-inter">
                          Brandkit
                        </span>
                      ) : isThemeCreatePage ? (
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => navigate('/brandkit')}
                            className="text-sm font-normal text-gray-900 font-inter hover:text-gray-600 transition-colors"
                          >
                            Brandkit
                          </button>
                          <ChevronRight size={16} className="text-gray-400 flex-shrink-0" />
                          <span className="text-sm font-normal text-gray-900 font-inter">
                            {activeBrandkit?.name || 'Brandkit'}
                          </span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => navigate('/brandkit')}
                            className="text-sm font-normal text-gray-900 font-inter hover:text-gray-600 transition-colors"
                          >
                            Brandkit
                          </button>
                          <ChevronRight size={16} className="text-gray-400 flex-shrink-0" />
                          <div className="flex items-center gap-1 group">
                            <span className="text-sm font-normal text-gray-900 font-inter">
                              {activeBrandkit?.name || 'Brandkit'}
                            </span>
                            {activeBrandkit?._id && (
                              <button
                                onClick={() => setIsBrandkitRenameModalOpen(true)}
                                className="p-1 rounded-md hover:bg-gray-100 transition-colors opacity-0 group-hover:opacity-100 focus:opacity-100"
                                aria-label="Rename brandkit"
                              >
                                <Edit2 size={14} className="text-gray-500" />
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Brandkit Tabs - centered */}
            {isBrandkitPage && location.pathname !== "/brandkit" && activeBrandkit?._id && (
              <div className="hidden md:flex absolute left-1/2 transform -translate-x-1/2">
                <div className="flex items-center h-16 gap-0">
                  {[
                    {
                      name: "Assets",
                      path: "",
                    },
                    {
                      name: "Themes",
                      path: "/themes",
                    },
                  ].map((tab) => {
                    const tabPath = `/brandkit/${activeBrandkit._id}${tab.path}`;
                    // Themes tab is active for both /themes and /themes/create pages
                    const isActive = tab.path === "" 
                      ? location.pathname === `/brandkit/${activeBrandkit._id}`
                      : location.pathname.startsWith(tabPath);
                    
                    return (
                      <Link
                        key={tab.name}
                        to={tabPath}
                        className={`flex items-center justify-center px-2 h-full text-sm font-normal transition-all border-t-2 pt-0 ${
                          isActive
                            ? "border-black text-black"
                            : "border-transparent text-gray-600 hover:text-gray-900"
                        }`}
                      >
                        {tab.name}
                      </Link>
                    );
                  })}
                </div>
              </div>
            )}


            <div className={`flex items-center h-full justify-end ${!shouldWrapTabs && isSurveyDesignerRoute && currentSurveyId ? 'flex-none' : 'flex-1'}`}>
              {user && !user.isAnonymous ? (
                <>
                  {location.pathname.includes("/survey/") &&
                    currentSurveyId && (
                      <div className="flex items-center gap-2 mx-2">
                        {saveStatus !== "idle" && currentTab !== "Results" && (
                          <div
                            className={`flex items-center gap-2 px-4 h-10 text-sm font-normal transition-colors
                            ${
                              saveStatus === "saved"
                                ? "text-green-700"
                                : "text-gray-700"
                            }`}
                            style={{ fontSize: '13px' }}
                          >
                            {saveStatus === "saved" && (
                              <div className="flex items-center gap-2" 
                                   style={{ 
                                     animation: 'fadeIn 0.3s ease-in-out forwards' 
                                   }}>
                                <IoCloudDoneOutline
                                  size={16}
                                  className="text-green-700"
                                />
                                <span className="font-normal text-green-700">Saved!</span>
                              </div>
                            )}
                            {saveStatus === "saving" && (
                              <span className="font-normal text-gray-700">Saving...</span>
                            )}
                          </div>
                        )}
                        {isCreateRoute && (
                          <div className="hidden md:flex items-center gap-2">
                            <Tooltip content="Undo" position="bottom">
                              <button
                                className="flex items-center justify-center w-10 h-10 border border-gray-300 rounded-[10px] hover:bg-gray-50 transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                                onClick={() => window.dispatchEvent(new CustomEvent("designerUndo"))}
                                disabled={!canUndo}
                                aria-label="Undo"
                              >
                                <Undo2 size={20} className="text-gray-600" />
                              </button>
                            </Tooltip>
                            <Tooltip content="Redo" position="bottom">
                              <button
                                className="flex items-center justify-center w-10 h-10 border border-gray-300 rounded-[10px] hover:bg-gray-50 transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                                onClick={() => window.dispatchEvent(new CustomEvent("designerRedo"))}
                                disabled={!canRedo}
                                aria-label="Redo"
                              >
                                <Redo2 size={20} className="text-gray-600" />
                              </button>
                            </Tooltip>
                          </div>
                        )}
                        {isCreateRoute && (
                          <Tooltip
                            content={`Switch to ${designerPreviewMode === "desktop" ? "mobile" : "desktop"} preview`}
                            position="bottom"
                          >
                            <button
                              className="hidden md:flex items-center justify-center w-10 h-10 border border-gray-300 rounded-[10px] hover:bg-gray-50 transition-all duration-200"
                              onClick={() => {
                                window.dispatchEvent(new CustomEvent("toggleDesignerPreviewMode"));
                              }}
                              aria-label={`Current preview: ${designerPreviewMode}. Switch preview mode`}
                            >
                              {designerPreviewMode === "desktop" ? (
                                <Monitor size={20} className="text-gray-600" />
                              ) : (
                                <Smartphone size={20} className="text-gray-600" />
                              )}
                            </button>
                          </Tooltip>
                        )}
                        <Tooltip content="Preview form" position="bottom">
                          <button
                            className="hidden md:flex items-center justify-center w-10 h-10 border border-gray-300 rounded-[10px] hover:bg-gray-50 transition-all duration-200"
                            onClick={() => {
                              // The save-flush listener (requestSaveFlush ->
                              // saveFlushComplete) only exists in SurveyDesigner,
                              // which is mounted on the Create tab. On other tabs
                              // nothing answers, so don't wait for the flush —
                              // open the preview immediately instead of stalling
                              // for the fallback timeout.
                              if (!isCreateRoute) {
                                setPreviewTimestamp(Date.now());
                                setShowPreviewModal(true);
                                return;
                              }
                              let opened = false;
                              const onFlush = () => {
                                if (opened) return;
                                opened = true;
                                window.removeEventListener('saveFlushComplete', onFlush);
                                setPreviewTimestamp(Date.now());
                                setShowPreviewModal(true);
                              };
                              window.addEventListener('saveFlushComplete', onFlush);
                              window.dispatchEvent(new CustomEvent('requestSaveFlush'));
                              setTimeout(onFlush, 3000);
                            }}
                          >
                            <Eye size={20} className="text-gray-600" />
                          </button>
                        </Tooltip>
                        {(hasChanges || isSurveyDesignerRoute) && currentTab !== "Results" && (
                          <Tooltip content="Share form" position="bottom">
                            <button
                              onClick={() => {
                                if (isCreateRoute) {
                                  // Dispatch event to open share modal in SurveyDesigner (only on Create tab)
                                  const shareEvent = new CustomEvent('openShareModal', {
                                    detail: { surveyId: surveyId }
                                  });
                                  window.dispatchEvent(shareEvent);
                                } else {
                                  // On Logic/Settings tabs, open modal directly in NavBar
                                  setShowShareModal(true);
                                }
                              }}
                              className="flex items-center justify-center gap-2 px-3 h-10 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors"
                            >
                              <PiArrowBendUpRight size={18} />
                              <span className="text-sm font-normal" style={{ fontSize: '14px' }}>Share</span>
                            </button>
                          </Tooltip>
                        )}
                      </div>
                    )}
                  
                  {/* Profile section - hidden on new survey route */}
                  <div className={`flex items-center gap-3 mr-4 ${isNewSurveyRoute ? 'hidden' : ''}`}>
                    {!isSurveyDesignerRoute && currentSubscription?.plan?.type === "free" && (
                      <button
                        onClick={handleUpgradeClick}
                        className="text-sm py-2 px-3 rounded-lg font-medium border"
                        style={{
                          background: 'linear-gradient(to top, #9AF2FF, #F4FAFA)',
                          borderColor: '#EEF4F6',
                          color: '#323A3D'
                        }}
                      >
                        View Plans
                      </button>
                    )}

                        {/* Brandkit link - only on workspaces page, desktop only */}
                        {isSurveysPage && (
                          <button
                            onClick={() => navigate('/brandkit')}
                            className="hidden md:flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                          >
                            <PaintbrushVertical size={18} />
                            <span className="text-sm font-normal">Brandkit</span>
                          </button>
                        )}

                        {(isSurveysPage || isProfilePage || (isBrandkitPage && !isThemeCreatePage)) && <Notification openUpgrade={handleUpgradeClick} />}
                        
                        {(isSurveysPage || isProfilePage || (isBrandkitPage && !isThemeCreatePage)) && (
                          <button
                            onClick={handleOpenWhatsNew}
                            className="relative hidden md:block"
                            title="What's new"
                          >
                            <div className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-gray-100 transition-colors">
                              <HelpCircle size={18} className="text-gray-600" />
                            </div>
                            {hasWhatsNewBadge && (
                              <span className="absolute top-0 right-0 w-2.5 h-2.5 bg-red-500 rounded-full ring-2 ring-white" />
                            )}
                          </button>
                        )}
                        
                        {isThemeCreatePage && (
                          <ThemePublishButton />
                        )}

                        {/* Wrap avatar and dropdown with the ref so inside clicks aren't considered outside */}
                        <div ref={profileDropdownRef} className="relative">
                          <button
                            onClick={() =>
                              setIsProfileDropdownOpen(!isProfileDropdownOpen)
                            }
                            className="focus:outline-none group relative flex items-center justify-center w-10 h-10"
                          >
                            <img
                              src={user?.photoURL || "/images/nouser.svg"}
                              alt="Profile"
                              className="w-8 h-8 rounded-full object-cover transition-all duration-200 transform group-hover:scale-105"
                              referrerPolicy="no-referrer"
                            />
                          </button>

                        {isProfileDropdownOpen && (
                          <div
                            className="absolute right-0 top-full mt-0 w-72 bg-white rounded-xl py-2 z-50 border border-line animate-dropdown-open"
                            style={{ 
                              zIndex: 99999999,
                              background: 'linear-gradient(135deg, #ffffff 0%, #F7F8FA 100%)',
                              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03)'
                            }}
                          >
                            <div className="px-4 py-2.5 border-b border-gray-100/80">
                              <div className="flex items-center space-x-3">
                                <img
                                  src={user?.photoURL || "/images/nouser.svg"}
                                  alt="Profile"
                                  className="w-7 h-7 rounded-full object-cover shadow-sm"
                                  referrerPolicy="no-referrer"
                                />
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-semibold text-gray-900 truncate">
                                    {user?.displayName || "User"}
                                  </p>
                                  <p className="text-xs text-gray-500 truncate">
                                    {user?.email}
                                  </p>
                                </div>
                              </div>
                            </div>

                            {/* Response Counter */}
                            <ResponseCounter variant="full" showUpgradeButton={true} />

                            <div className="py-1.5">
                              <ProfileDropdownItem
                                icon={House}
                                label="Home"
                                onClick={() => {
                                  navigate('/workspaces');
                                  setIsProfileDropdownOpen(false);
                                }}
                              />
                              <ProfileDropdownItem
                                icon={User}
                                label="My profile"
                                onClick={() => {
                                  navigate('/profile?tab=profile');
                                  setIsProfileDropdownOpen(false);
                                }}
                              />
                              <ProfileDropdownItem
                                icon={Users}
                                label="Members"
                                onClick={() => {
                                  navigate('/profile?tab=members');
                                  setIsProfileDropdownOpen(false);
                                }}
                              />
                              <ProfileDropdownItem
                                icon={CreditCard}
                                label="Subscription & Billing"
                                onClick={() => {
                                  navigate('/profile?tab=billing');
                                  setIsProfileDropdownOpen(false);
                                }}
                              />
                              <ProfileDropdownItem
                                icon={Key}
                                label="API Keys"
                                onClick={() => {
                                  navigate('/profile?tab=api-keys');
                                  setIsProfileDropdownOpen(false);
                                }}
                              />
                              <ProfileDropdownItem
                                icon={PaintbrushVertical}
                                label="Brandkit"
                                onClick={() => {
                                  navigate('/brandkit');
                                  setIsProfileDropdownOpen(false);
                                }}
                              />
                            </div>

                            <div className="border-t border-gray-100/80 py-1.5">
                              <ProfileDropdownItem
                                icon={LogOut}
                                label="Log out"
                                onClick={handleLogout}
                              />
                            </div>
                          </div>
                        )}
                        </div>
                  </div>
                </>
              ) : (
                <button
                  onClick={() => navigate('/auth')}
                  className="bg-blue-500 text-white py-2 px-4 rounded hover:bg-blue-600 transition-colors mr-4"
                >
                  Sign In
                </button>
              )}
            </div>
          </nav>
        )}

        {/* Mobile tabs - below navbar (only show when tabs are not wrapped) */}
        {isSurveyDesignerRoute && currentSurveyId && !shouldWrapTabs && (
          <div className="md:hidden border-b border-gray-200">
            <div className="flex items-center justify-around h-12 px-2">
              {[
                {
                  name: "Create",
                  to: `/survey/${currentSurveyId}`,
                },
                {
                  name: "Logic",
                  to: `/survey/${currentSurveyId}/logic`,
                },
                {
                  name: "Settings",
                  to: `/survey/${currentSurveyId}/settings`,
                },
                {
                  name: "Results",
                  to: `/survey/${currentSurveyId}/analytics`,
                },
              ].map((elem) => {
                return (
                  <SurveyTabs
                    key={elem.name}
                    elem={elem}
                    currentTab={currentTab}
                    isReachedLimit={isReachedLimit}
                    responseCount={activeSurvey?.responseCount}
                  />
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Share Modal - show when not on Create tab (Logic/Settings tabs handle it here) */}
      {!isCreateRoute && (
        <ModalContainer
          isOpen={showShareModal}
          onClose={() => setShowShareModal(false)}
          title="Share your form to get responses"
          size="md"
          hideHeaderBorder={true}
        >
          <ShareSurveyModal
            surveyId={surveyId || currentSurveyId}
            surveyLink={`${window.location.origin}/s/${surveyId || currentSurveyId}`}
            handleCopyLink={handleCopyLink}
            onClose={() => setShowShareModal(false)}
          />
        </ModalContainer>
      )}

      {/* Full-screen Preview Modal */}
      {showPreviewModal && createPortal(
        <div className="fixed inset-0 z-[10001] bg-surface-page flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 bg-white border-b border-gray-200">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setShowPreviewModal(false)}
                className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
              >
                <X className="w-5 h-5" />
                <span className="text-sm font-medium">Close preview</span>
              </button>
            </div>
            
            {/* Device Toggle - Center */}
            <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setPreviewDevice('desktop')}
                className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
                  previewDevice === 'desktop' 
                    ? 'bg-white text-gray-900 shadow-sm' 
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <Monitor className="w-4 h-4" />
                <span>Desktop</span>
              </button>
              <button
                onClick={() => setPreviewDevice('mobile')}
                className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
                  previewDevice === 'mobile' 
                    ? 'bg-white text-gray-900 shadow-sm' 
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <Smartphone className="w-4 h-4" />
                <span>Mobile</span>
              </button>
            </div>
            
            {/* Right side - Share button */}
            <div className="flex items-center">
              <button
                onClick={() => {
                  if (isCreateRoute) {
                    // Create tab owns the share modal because it can include unsaved designer state.
                    const shareEvent = new CustomEvent('openShareModal', {
                      detail: { surveyId: currentSurveyId }
                    });
                    window.dispatchEvent(shareEvent);
                  } else {
                    setShowShareModal(true);
                  }
                }}
                className="flex items-center justify-center gap-2 px-4 h-10 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors"
              >
                <PiArrowBendUpRight size={18} />
                <span className="text-sm font-medium">Share</span>
              </button>
            </div>
          </div>

          {/* Preview Content */}
          <div className="flex-1 flex items-center justify-center p-8 overflow-hidden">
            <div 
              className={`relative bg-white rounded-2xl overflow-hidden transition-all duration-300 ${
                previewDevice === 'mobile' 
                  ? 'w-[390px] h-[844px] max-h-[calc(100vh-140px)]' 
                  : 'w-full max-w-[calc((100vh-140px)*16/9)] aspect-[16/9] max-h-[calc(100vh-140px)]'
              }`}
            >
              {/* Inner border overlay */}
              <div 
                className="absolute inset-0 pointer-events-none rounded-2xl z-10"
                style={{ boxShadow: 'inset 0 0 0 1px rgba(35, 46, 81, 0.1)' }}
              />
              <iframe 
                src={`/s/${currentSurveyId}?preview=1&t=${previewTimestamp}`}
                className="w-full h-full border-0"
                title="Survey Preview"
              />
            </div>
          </div>
          
          {/* Footer note */}
          <div className="text-center py-3 text-sm text-gray-500">
            Preview mode — responses are not saved
          </div>
        </div>,
        document.body
      )}

      <ModalContainer
        isOpen={isPricingModalOpen}
        onClose={() => setIsPricingModalOpen(false)}
        title=""
        className=""
        size="5xl"
      >
        <div className="min-h-0 max-h-[calc(100vh-8rem)] overflow-y-auto px-20 pb-6">
          <div className="text-app-black-800 text-4xl font-bold mb-8 text-center ">
            Your Time to get some Answers
          </div>
          <BillingTab usingFor="VIEW-PLANS" />
        </div>
      </ModalContainer>

      <SurveyRenameModal
        isOpen={isSurveyRenameModalOpen}
        onClose={() => setIsSurveyRenameModalOpen(false)}
        onSubmit={handleRenameSubmit}
        surveyData={activeSurvey}
      />

      <SurveyRenameModal
        isOpen={isBrandkitRenameModalOpen}
        onClose={() => setIsBrandkitRenameModalOpen(false)}
        onSubmit={(id, title) => handleBrandkitRenameSubmit(id, title)}
        surveyData={
          activeBrandkit?._id
            ? { surveyId: String(activeBrandkit._id), title: activeBrandkit.name }
            : null
        }
      />

      <WhatsNewPopup
        isOpen={isWhatsNewOpen}
        onClose={() => setIsWhatsNewOpen(false)}
      />
    </>
  );
});

const SurveyTabs = ({ isReachedLimit, elem, currentTab, responseCount }) => {
  const isActive = currentTab === elem.name;
  return isReachedLimit && elem.name !== "Results" ? (
    <span className="flex items-center justify-center px-2 h-full text-xs md:text-sm font-normal transition-all text-gray-400 cursor-not-allowed">
      <span className="relative inline-flex items-center justify-center h-full">
        <span className="relative z-10">{elem.name}</span>
      </span>
    </span>
  ) : (
    <Link
      key={elem.name}
      to={elem.to}
      className={`group relative flex items-center justify-center px-2 h-full text-xs md:text-sm font-normal transition-all ${
        isActive ? "text-black" : "text-gray-600 hover:text-gray-900"
      }`}
    >
      <span className="relative inline-flex items-center justify-center h-full">
        {/* gray rounded background behind the active tab text */}
        <span
          className={`absolute top-1/2 -translate-y-1/2 -inset-x-3 h-8 rounded-lg transition-colors ${
            isActive ? "bg-gray-100" : "group-hover:bg-gray-50"
          }`}
        />
        {/* indicator line glued to the top of the navbar (bottom on mobile), 60% of text width */}
        {isActive && (
          <span className="absolute left-1/2 -translate-x-1/2 bottom-0 md:bottom-auto md:top-0 w-[90%] h-0.5 bg-black rounded-full" />
        )}
        <span className="relative z-10">{elem.name}</span>
      </span>
    </Link>
  );
};

export default NavBar;
